import React, { useRef, useEffect } from "react";
import { Switch, Popover } from "antd";
import { Button } from "../buttons";
import { Ban, CircleCheck, CircleX, CircleAlert } from "lucide-react";
import { Icon } from "src/components/icons/Icon";
import { HospitalSymbol } from "src/components/icons/custom";

export interface ISwitchFilter {
	[keys: string]: any;
	pass: boolean;
	fail: boolean;
	hazardous: boolean;
	borderline: boolean;
	lowRisk: boolean;
	rangeOfInquiry: boolean;
	highRisk: boolean;
	indeterminate: boolean;
	completed: boolean;
	notCompleted: boolean;
	aborted: boolean;
}

interface IProps {
	name: string;
	values: ISwitchFilter;
	shouldDisplayFilter: boolean;
	displayFilterName: string;
	closeDisplayFilter: (name: string) => void;
	showOrHideDisplayFilters: (name: string) => void;
	convertForFilter: (switchType: any, name: string, onChange: any) => void;
	onChange: (item: any) => void;
}

const getIconForFilter = (filter: string, style?: any) => {
	if (filter === "pass" || filter === "lowRisk" || filter === "completed") {
		return (
			<Icon icon={CircleCheck} badge style={{ ...style, color: "#027F01" }} />
		);
	} else if (
		filter === "fail" ||
		filter === "highRisk" ||
		filter === "notCompleted"
	) {
		return (
			<Icon icon={CircleX} badge style={{ ...style, color: "#FA0B02" }} />
		);
	} else if (filter === "hazardous") {
		return (
			<Icon
				icon={HospitalSymbol}
				badge
				style={{ ...style, color: "#FA0B02" }}
			/>
		);
	} else if (
		filter === "borderline" ||
		filter === "rangeOfInquiry" ||
		filter === "indeterminate"
	) {
		return (
			<Icon icon={CircleAlert} badge style={{ ...style, color: "#FCA900" }} />
		);
	} else if (filter === "aborted") {
		return <Icon icon={Ban} style={{ ...style, color: "#092a58" }} />;
	}
	throw Error("Unknown filter type");
};

const SwitchFilter: React.FC<IProps> = (props) => {
	const {
		name,
		values,
		shouldDisplayFilter,
		displayFilterName,
		showOrHideDisplayFilters,
		convertForFilter,
		onChange,
		closeDisplayFilter,
	} = props;

	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const popoverRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent | TouchEvent) => {
			// Check if click is outside the popover content
			if (
				popoverRef.current &&
				!popoverRef.current.contains(event.target as Node)
			) {
				if (shouldDisplayFilter) {
					closeDisplayFilter(displayFilterName);
				}
			}
		};

		if (shouldDisplayFilter) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("touchend", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("touchend", handleClickOutside);
		};
	}, [shouldDisplayFilter, props, displayFilterName]);

	const refOnClick = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation(); // Prevent event bubbling
	};

	const transformedSwitchValues = Object.keys(values).map((key) => {
		const label =
			`${key[0].toUpperCase()}${key.slice(1)}` && key === "rangeOfInquiry"
				? "Range of Inquiry"
				: key[0].toUpperCase() + key.slice(1);
		return (
			<div className="d-flex" key={key}>
				{label}
				<Switch
					className="ml-auto pl-3"
					checked={values[key]}
					checkedChildren={getIconForFilter(key)}
					unCheckedChildren={getIconForFilter(key)}
					onChange={() => convertForFilter(key, name, onChange)}
				/>
			</div>
		);
	});

	const transformSwitchesWithIcon = (
		switches: JSX.Element[],
		props: IProps
	) => {
		return <div>{switches}</div>;
	};

	const modifiedSwitches = transformSwitchesWithIcon(
		transformedSwitchValues,
		props
	);

	const defaultLabelAndSwitch = (
		modifiedSwitches: JSX.Element,
		props: IProps
	) => {
		return (
			<div className="d-flex">
				<Popover
					content={<div ref={popoverRef}>{modifiedSwitches}</div>}
					title="Filter by: "
					visible={shouldDisplayFilter}
				>
					<Button
						className="btn-outline-primary btn-block p-2"
						onClick={() => showOrHideDisplayFilters(displayFilterName)}
					>
						{filteredSwitchButton(values)}
					</Button>
				</Popover>
			</div>
		);
	};

	const filteredSwitchButton = (values: ISwitchFilter) => {
		const getDisplayStyle = (displayed: boolean) => {
			return displayed ? {} : { display: "none" };
		};

		if (
			!values.pass &&
			!values.borderline &&
			!values.hazardous &&
			!values.fail &&
			!values.lowRisk &&
			!values.rangeOfInquiry &&
			!values.highRisk &&
			!values.indeterminate &&
			!values.completed &&
			!values.notCompleted &&
			!values.aborted
		) {
			return <span>All</span>;
		}
		return (
			<>
				{getIconForFilter("pass", getDisplayStyle(values.pass))}
				{getIconForFilter("fail", getDisplayStyle(values.fail))}
				{getIconForFilter("hazardous", getDisplayStyle(values.hazardous))}
				{getIconForFilter("borderline", getDisplayStyle(values.borderline))}
				{getIconForFilter("lowRisk", getDisplayStyle(values.lowRisk))}
				{getIconForFilter(
					"rangeOfInquiry",
					getDisplayStyle(values.rangeOfInquiry)
				)}
				{getIconForFilter("highRisk", getDisplayStyle(values.highRisk))}
				{getIconForFilter(
					"indeterminate",
					getDisplayStyle(values.indeterminate)
				)}
				{getIconForFilter("completed", getDisplayStyle(values.completed))}
				{getIconForFilter("notCompleted", getDisplayStyle(values.notCompleted))}
				{getIconForFilter("aborted", getDisplayStyle(values.aborted))}
			</>
		);
	};

	return (
		<React.Fragment>
			{defaultLabelAndSwitch(modifiedSwitches, props)}
		</React.Fragment>
	);
};

export default SwitchFilter;
